import { type ReactNode, useState } from 'react';
import type { VariantProps } from 'class-variance-authority';

import type { buttonVariants } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  type DialogContentProps,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type ConfirmColorScheme = NonNullable<VariantProps<typeof buttonVariants>['colorScheme']>;
type ConfirmVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  size?: DialogContentProps['size'];
  confirmLabel?: string;
  cancelLabel?: string;
  loadingLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  onError?: (error: unknown) => void;
  confirmColorScheme?: ConfirmColorScheme;
  confirmVariant?: ConfirmVariant;
  confirmDisabled?: boolean;
  preventCloseDuringLoading?: boolean;
  hideCancel?: boolean;
  showCloseButton?: boolean;
  stopPointerDownPropagation?: boolean;
  contentClassName?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  size = 'sm',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loadingLabel,
  onConfirm,
  onCancel,
  onError,
  confirmColorScheme = 'primary',
  confirmVariant = 'outline',
  confirmDisabled = false,
  preventCloseDuringLoading = true,
  hideCancel = false,
  showCloseButton = true,
  stopPointerDownPropagation = false,
  contentClassName,
}: ConfirmDialogProps) {
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    setIsConfirmLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      // Don't close dialog on error - let the user retry or cancel manually
      onError?.(error);
      console.error('Confirm action failed:', error);
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (preventCloseDuringLoading && isConfirmLoading && !newOpen) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size={size}
        className={contentClassName}
        dismissible={!isConfirmLoading || !preventCloseDuringLoading}
        showCloseButton={showCloseButton}
        onPointerDown={stopPointerDownPropagation ? (e) => e.stopPropagation() : undefined}
      >
        <DialogHeader className="gap-2">
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription asChild className="text-foreground">
              <div>{description}</div>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter className="mt-4">
          {!hideCancel && (
            <Button
              variant="outline"
              colorScheme="secondary"
              onClick={handleCancel}
              disabled={isConfirmLoading}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={confirmVariant}
            colorScheme={confirmColorScheme}
            onClick={handleConfirm}
            isLoading={isConfirmLoading}
            disabled={confirmDisabled}
            autoFocus
          >
            {isConfirmLoading && loadingLabel ? loadingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
